import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  Microsoft as MicrosoftIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { mailKontenApi, m365AuthApi, m365ConfigApi } from '../services/api';
import type { MailKonto } from '../types/api';

const MailKonten: React.FC = () => {
  const [mailKonten, setMailKonten] = useState<MailKonto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [m365ConfigDialogOpen, setM365ConfigDialogOpen] = useState(false);
  const [m365Config, setM365Config] = useState<any>(null);
  const [connecting, setConnecting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKonto, setNewKonto] = useState({
    kontoName: '',
    emailAdresse: '',
    imapServer: '',
    imapPort: 993,
    imapSsl: true,
    imapBenutzer: '',
    imapPasswort: '',
    smtpServer: '',
    smtpPort: 587,
    smtpSsl: true,
    smtpBenutzer: '',
    smtpPasswort: '',
    aktiv: true,
    standardKonto: false,
  });

  useEffect(() => {
    loadMailKonten();
  }, []);

  const loadMailKonten = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await mailKontenApi.getAll();
      setMailKonten(response.data);
    } catch (err: any) {
      console.error('Mail-Konten-Fehler:', err);
      setError(
        err.response?.data?.message || 'Fehler beim Laden der Mail-Konten'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConnectM365 = async () => {
    try {
      setConnecting(true);
      const response = await m365AuthApi.startOAuthFlow();

      // Zeige Config-Source
      console.log(`Using M365 config from: ${response.data.configSource}`);

      // Redirect zu Microsoft OAuth
      window.location.href = response.data.authorizationUrl;
    } catch (err: any) {
      console.error('M365 Auth-Fehler:', err);
      alert(err.response?.data?.message || 'Fehler beim Starten der Microsoft 365 Authentifizierung');
    } finally {
      setConnecting(false);
    }
  };

  const handleRefreshToken = async (kontoId: number) => {
    try {
      await m365AuthApi.refreshToken(kontoId);
      alert('Token erfolgreich erneuert');
      loadMailKonten();
    } catch (err: any) {
      alert('Fehler beim Erneuern des Tokens: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleTestConnection = async (kontoId: number) => {
    try {
      const response = await m365AuthApi.testConnection(kontoId);
      alert(`Verbindung erfolgreich!\n\nProfil: ${JSON.stringify(response.data.profile, null, 2)}`);
    } catch (err: any) {
      alert('Verbindung fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSetStandard = async (kontoId: number) => {
    try {
      await mailKontenApi.setStandard(kontoId);
      setMailKonten(
        mailKonten.map((k) => ({
          ...k,
          istStandard: k.kontoID === kontoId,
        }))
      );
    } catch (err: any) {
      alert('Fehler beim Setzen des Standard-Kontos');
    }
  };

  const handleDelete = async (kontoId: number) => {
    if (!window.confirm('Mail-Konto wirklich löschen?')) return;

    try {
      await mailKontenApi.delete(kontoId);
      setMailKonten(mailKonten.filter((k) => k.kontoID !== kontoId));
    } catch (err: any) {
      alert('Fehler beim Löschen des Mail-Kontos');
    }
  };

  const loadM365Config = async () => {
    try {
      const response = await m365ConfigApi.getCurrent();
      setM365Config(response.data);
      setM365ConfigDialogOpen(true);
    } catch (err: any) {
      alert('Fehler beim Laden der Microsoft 365 Konfiguration: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCreateKonto = async () => {
    try {
      await mailKontenApi.create(newKonto);
      setCreateDialogOpen(false);
      setNewKonto({
        kontoName: '',
        emailAdresse: '',
        imapServer: '',
        imapPort: 993,
        imapSsl: true,
        imapBenutzer: '',
        imapPasswort: '',
        smtpServer: '',
        smtpPort: 587,
        smtpSsl: true,
        smtpBenutzer: '',
        smtpPasswort: '',
        aktiv: true,
        standardKonto: false,
      });
      loadMailKonten();
      alert('Mail-Konto erfolgreich erstellt');
    } catch (err: any) {
      alert('Fehler beim Erstellen des Mail-Kontos: ' + (err.response?.data?.message || err.message));
    }
  };

  const getKontoTypeIcon = (kontoTyp: string) => {
    switch (kontoTyp) {
      case 'Microsoft365':
      case 'Outlook':
        return <MicrosoftIcon color="primary" />;
      default:
        return <SendIcon />;
    }
  };

  const getTokenStatus = (konto: MailKonto) => {
    if (konto.kontoTyp !== 'Microsoft365' && konto.kontoTyp !== 'Outlook') {
      return null;
    }

    if (!konto.tokenExpiry) {
      return <Chip label="Kein Token" size="small" color="error" />;
    }

    const expiry = new Date(konto.tokenExpiry);
    const now = new Date();
    const isExpired = expiry <= now;

    if (isExpired) {
      return <Chip label="Token abgelaufen" size="small" color="error" icon={<ErrorIcon />} />;
    }

    const hoursUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (hoursUntilExpiry < 1) {
      return <Chip label={`${Math.round(hoursUntilExpiry * 60)} Min`} size="small" color="warning" icon={<ErrorIcon />} />;
    }

    return <Chip label={`${Math.round(hoursUntilExpiry)}h gültig`} size="small" color="success" icon={<CheckCircleIcon />} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            Mail-Konten
          </Typography>
          <Box>
            <Button
              variant="outlined"
              onClick={loadM365Config}
              startIcon={<MicrosoftIcon />}
              sx={{ mr: 1 }}
            >
              M365 Config
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<AddIcon />}
              sx={{ mr: 1 }}
            >
              Neues Mail-Konto
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConnectM365}
              startIcon={<MicrosoftIcon />}
              disabled={connecting}
            >
              {connecting ? 'Verbinde...' : 'Microsoft 365 verbinden'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>E-Mail-Adresse</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Token-Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mailKonten.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">
                      Keine Mail-Konten vorhanden
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Klicken Sie auf "Microsoft 365 verbinden", um ein Konto hinzuzufügen
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                mailKonten.map((konto) => (
                  <TableRow key={konto.kontoID}>
                    <TableCell>
                      <Tooltip title={konto.kontoTyp}>
                        {getKontoTypeIcon(konto.kontoTyp)}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {konto.kontoName}
                        {konto.istStandard && (
                          <Chip
                            label="Standard"
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{konto.emailAdresse}</TableCell>
                    <TableCell>
                      {konto.istAktiv ? (
                        <Chip label="Aktiv" size="small" color="success" />
                      ) : (
                        <Chip label="Inaktiv" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      {getTokenStatus(konto)}
                    </TableCell>
                    <TableCell align="right">
                      {!konto.istStandard && (
                        <Tooltip title="Als Standard setzen">
                          <IconButton
                            size="small"
                            onClick={() => handleSetStandard(konto.kontoID)}
                          >
                            <StarBorderIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {(konto.kontoTyp === 'Microsoft365' || konto.kontoTyp === 'Outlook') && (
                        <>
                          <Tooltip title="Token erneuern">
                            <IconButton
                              size="small"
                              onClick={() => handleRefreshToken(konto.kontoID)}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Verbindung testen">
                            <IconButton
                              size="small"
                              onClick={() => handleTestConnection(konto.kontoID)}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(konto.kontoID)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Neues Mail-Konto Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Neues IMAP Mail-Konto erstellen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Allgemeine Einstellungen</Typography>
            <TextField
              label="Konto-Name"
              fullWidth
              margin="normal"
              value={newKonto.kontoName}
              onChange={(e) => setNewKonto({ ...newKonto, kontoName: e.target.value })}
              placeholder="z.B. Mein E-Mail-Konto"
            />
            <TextField
              label="E-Mail-Adresse"
              fullWidth
              margin="normal"
              type="email"
              value={newKonto.emailAdresse}
              onChange={(e) => setNewKonto({ ...newKonto, emailAdresse: e.target.value })}
              placeholder="name@example.com"
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>IMAP Einstellungen (Empfang)</Typography>
            <TextField
              label="IMAP Server"
              fullWidth
              margin="normal"
              value={newKonto.imapServer}
              onChange={(e) => setNewKonto({ ...newKonto, imapServer: e.target.value })}
              placeholder="imap.example.com"
            />
            <Box display="flex" gap={2}>
              <TextField
                label="IMAP Port"
                type="number"
                margin="normal"
                value={newKonto.imapPort}
                onChange={(e) => setNewKonto({ ...newKonto, imapPort: parseInt(e.target.value) })}
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newKonto.imapSsl}
                    onChange={(e) => setNewKonto({ ...newKonto, imapSsl: e.target.checked })}
                  />
                }
                label="SSL verwenden"
                sx={{ flex: 1, mt: 2 }}
              />
            </Box>
            <TextField
              label="IMAP Benutzername"
              fullWidth
              margin="normal"
              value={newKonto.imapBenutzer}
              onChange={(e) => setNewKonto({ ...newKonto, imapBenutzer: e.target.value })}
            />
            <TextField
              label="IMAP Passwort"
              fullWidth
              margin="normal"
              type="password"
              value={newKonto.imapPasswort}
              onChange={(e) => setNewKonto({ ...newKonto, imapPasswort: e.target.value })}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>SMTP Einstellungen (Versand)</Typography>
            <TextField
              label="SMTP Server"
              fullWidth
              margin="normal"
              value={newKonto.smtpServer}
              onChange={(e) => setNewKonto({ ...newKonto, smtpServer: e.target.value })}
              placeholder="smtp.example.com"
            />
            <Box display="flex" gap={2}>
              <TextField
                label="SMTP Port"
                type="number"
                margin="normal"
                value={newKonto.smtpPort}
                onChange={(e) => setNewKonto({ ...newKonto, smtpPort: parseInt(e.target.value) })}
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newKonto.smtpSsl}
                    onChange={(e) => setNewKonto({ ...newKonto, smtpSsl: e.target.checked })}
                  />
                }
                label="SSL verwenden"
                sx={{ flex: 1, mt: 2 }}
              />
            </Box>
            <TextField
              label="SMTP Benutzername"
              fullWidth
              margin="normal"
              value={newKonto.smtpBenutzer}
              onChange={(e) => setNewKonto({ ...newKonto, smtpBenutzer: e.target.value })}
            />
            <TextField
              label="SMTP Passwort"
              fullWidth
              margin="normal"
              type="password"
              value={newKonto.smtpPasswort}
              onChange={(e) => setNewKonto({ ...newKonto, smtpPasswort: e.target.value })}
            />

            <Divider sx={{ my: 3 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={newKonto.aktiv}
                  onChange={(e) => setNewKonto({ ...newKonto, aktiv: e.target.checked })}
                />
              }
              label="Konto aktiv"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newKonto.standardKonto}
                  onChange={(e) => setNewKonto({ ...newKonto, standardKonto: e.target.checked })}
                />
              }
              label="Als Standard-Konto verwenden"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateKonto}
            variant="contained"
            color="primary"
            disabled={
              !newKonto.kontoName ||
              !newKonto.emailAdresse ||
              !newKonto.imapServer ||
              !newKonto.smtpServer
            }
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Microsoft 365 Config Dialog */}
      <Dialog
        open={m365ConfigDialogOpen}
        onClose={() => setM365ConfigDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Microsoft 365 Konfiguration</DialogTitle>
        <DialogContent>
          {m365Config && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Konfigurationsquelle:</Typography>
                <Typography variant="body2"><strong>{m365Config.source}</strong></Typography>
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Client ID:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                  {m365Config.clientId || 'Nicht konfiguriert'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Client Secret:</Typography>
                <Typography variant="body2">
                  {m365Config.hasClientSecret ? '✓ Konfiguriert (wird nicht angezeigt)' : '✗ Nicht konfiguriert'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Tenant ID:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                  {m365Config.tenantId}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Redirect URI:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1, wordBreak: 'break-all' }}>
                  {m365Config.redirectUri}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>App Name:</Typography>
                <Typography variant="body2">
                  {m365Config.appName}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Alert severity={m365Config.isConfigured ? 'success' : 'warning'}>
                {m365Config.isConfigured
                  ? 'Microsoft 365 ist vollständig konfiguriert'
                  : 'Microsoft 365 ist nicht vollständig konfiguriert'}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setM365ConfigDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MailKonten;
